import Seo from '@/components/Seo';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Sparkles, Quote, Star } from 'lucide-react';
import BookCard from '@/components/BookCard';
import { getFeaturedBooks, getBooks, seedData } from '@/lib/api';

const HomePage = () => {
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await seedData();
        
        const [featured, all] = await Promise.all([
          getFeaturedBooks(),
          getBooks()
        ]);
        setFeaturedBooks(featured);
        setAllBooks(all);
        const flat = all.flatMap((b) =>
          (b.reviews || []).map((r) => ({ ...r, bookTitle: b.title, bookId: b.slug || b.id }))
        );
        setReviews(flat);
      } catch (error) {
        console.error('Error fetching books:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Seo description="Ink & Innovation Printworks — independent publisher of bold, historically-rich literary fiction by author Kavi Ocktiiv. Discover The Omission of Billy Seldom, Shadows in the Magnolias, and forthcoming titles." />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1224859/pexels-photo-1224859.jpeg"
            alt="Library background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="hero-overlay absolute inset-0" />
          <div className="gold-radial absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="overline text-gold mb-6">Ink and Innovation Printworks</p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-semibold mb-6 text-balance leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Stories That<br />
              <span className="text-gold">Illuminate</span>
            </h1>
            <p className="text-lg text-white/60 max-w-xl mx-auto mb-10 leading-relaxed">
              Ink and Innovation is home to bold literary fiction, poetry, and voices that challenge the ordinary. 
              Enter a world where every page holds discovery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/books"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-explore-btn"
              >
                Explore the Collection
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/author"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="hero-author-btn"
              >
                Meet the Author
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-gold/50 to-transparent" />
        </motion.div>
      </section>

      {/* Featured Books Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="overline text-gold mb-4">New & Notable</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <h2 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Featured Works
              </h2>
              <Link
                to="/books"
                className="text-white/50 hover:text-gold transition-colors inline-flex items-center gap-2 text-sm uppercase tracking-widest"
                data-testid="view-all-books"
              >
                View All
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[2/3] bg-white/5 img-loading" />
                  <div className="h-4 bg-white/5 w-1/3 img-loading" />
                  <div className="h-6 bg-white/5 w-2/3 img-loading" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBooks.slice(0, 3).map((book, index) => (
                <BookCard key={book.id} book={book} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 lg:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_e293d543-b110-4e1a-b256-18d454b4a060/artifacts/tm505qh6_suKgT.jpg"
                  alt="Kavi Ocktiiv"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-gold/30" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="overline text-gold">About the Author</p>
              <h2 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Kavi Ocktiiv
              </h2>
              <p className="text-white/60 leading-relaxed">
                Kavi Ocktiiv (Kah-vee ak-tev) is a proud Louisiana native, novelist, playwright, and music 
                engineer whose work is rooted in memory, omission, and cultural inheritance. Tracing his lineage 
                back to 1576, he uncovered profound Indigenous connections to American soil—a discovery that 
                ignited his mission of historical excavation and storytelling.
              </p>
              <p className="text-white/60 leading-relaxed">
                As the founder of Ink and Innovation, Kavi champions independent voices and historically 
                marginalized stories. He is also founder and director of the Portal of Commencement Midpoint 
                Shelter (POCMS) in Atlanta, a sanctuary for women and children.
              </p>
              <Link
                to="/author"
                className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors text-sm uppercase tracking-widest pt-4"
                data-testid="read-more-author"
              >
                Read Full Bio
                <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="gold-radial absolute inset-0" />
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="w-8 h-8 text-gold mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Begin Your Journey
            </h2>
            <p className="text-white/60 mb-10 max-w-xl mx-auto">
              Discover stories that will stay with you long after the final page. 
              Browse our collection of literary fiction, poetry, and more.
            </p>
            <Link
              to="/books"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="cta-browse-btn"
            >
              <BookOpen size={18} />
              Browse the Bookstore
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Latest from Journal */}
      <section className="py-24 lg:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="overline text-gold mb-4">Reader Reviews</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <h2 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                What Readers Are Saying
              </h2>
              <Link
                to="/reviews"
                className="text-white/50 hover:text-gold transition-colors inline-flex items-center gap-2 text-sm uppercase tracking-widest"
                data-testid="view-all-posts"
              >
                View All Reviews
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>

          {reviews.length === 0 ? (
            <div className="card-surface p-10 lg:p-12 text-center" data-testid="reviews-empty">
              <Quote className="w-8 h-8 text-gold/40 mx-auto mb-4" />
              <p className="text-white/50 max-w-lg mx-auto">
                Reader reviews for our titles are coming soon. Check back shortly to see what people are
                saying about Kavi Ocktiiv's novels.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {reviews.slice(0, 2).map((review, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card-surface p-8 lg:p-10"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={i < review.rating ? 'text-gold fill-gold' : 'text-white/20'}
                      />
                    ))}
                  </div>
                  <h3 className="text-xl lg:text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {review.title}
                  </h3>
                  <p className="text-white/50 mb-6 line-clamp-4 italic">"{review.text}"</p>
                  <Link
                    to={`/books/${review.bookId}`}
                    className="text-sm text-white/40 hover:text-gold transition-colors"
                    data-testid={`home-review-${index + 1}`}
                  >
                    — {review.reviewer}{review.location && `, ${review.location}`} · <span className="text-gold/70">{review.bookTitle}</span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
